<template>
  <div>
    <section-title
      content="3. Historial Educativo"
      icon="fa fa-book-user"
      reverse
    />
    <div class="educational-history-info">
      <section class="section-panel">
        <div class="section-panel__title">
          Experiencias Académicas y Vocacionales
        </div>
        <div class="section-panel__body">
          <div class="boolean-question">
            <p class="boolean-question__label">
              1. ¿Se encuentra estudiando actualmente?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.isCurrentlyStudying"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="currently-studying"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>

          <div class="grade-field">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.completedGradeProgram"
                :custom-error-msg="
                  'Favor de seleccionar el último grado o programa completado.'
                "
                :disabled="readOnly"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="2. Último grado o programa completado:"
                :options="completedGradeProgramOptions"
                placeholder="Seleccione"
              />
            </validation-provider>
          </div>

          <custom-text-area
            v-model="model.vocationalOccupationalTraining"
            class="question-textarea"
            :disabled="readOnly"
            label="3. Adiestramiento vocacional ocupacional o especial:"
            :maxlength="5000"
            placeholder=""
            show-char-count
            :count-classes="['d-block text-right']"
          />

          <custom-text-area
            v-model="model.otherAcademicExperiences"
            class="question-textarea question-textarea--spaced"
            :disabled="readOnly"
            label="4. Otras experiencias académicas:"
            :maxlength="5000"
            placeholder=""
            show-char-count
            :count-classes="['d-block text-right']"
          />

          <custom-text-area
            v-model="model.studentVocationalInterest"
            class="question-textarea question-textarea--spaced"
            :disabled="readOnly"
            label="5. Interés vocacional del estudiante:"
            :maxlength="5000"
            placeholder=""
            show-char-count
            :count-classes="['d-block text-right']"
          />

          <div class="boolean-question report-question">
            <p class="boolean-question__label">
              ¿Cuenta con un informe de apreciación o evaluación de intereses
              vocacionales?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.hasVocationalInterestEvaluationReport"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="vocational-interest-report"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>

          <div
            v-if="model.hasVocationalInterestEvaluationReport"
            class="document-row"
          >
            <div class="document-type">
              <label class="document-row__label">Tipo de Documento:</label>
              <p class="document-row__value">
                {{ model.vocationalInterestEvaluationDocumentType }}
              </p>
            </div>
            <div class="document-file">
              <label class="document-row__label">Archivo:</label>
              <validation-provider
                v-slot="{ errors }"
                :rules="
                  model.vocationalInterestEvaluationDocument ? '' : 'required'
                "
              >
                <custom-file-input
                  id="vocationalInterestEvaluationFile"
                  v-model="model.vocationalInterestEvaluationFile"
                  display-style="normal"
                  :accept="'.pdf'"
                  :attachment-id="
                    model.vocationalInterestEvaluationDocument?.documentId
                  "
                  custom-error-msg="Favor seleccionar un documento."
                  :disabled="readOnly"
                  :error="errors[0]"
                  label-position="left"
                  :name="model.vocationalInterestEvaluationDocument?.fileName"
                  :on-download="downloadVocationalInterestDocument"
                  @after-remove="removeVocationalInterestDocument"
                />
              </validation-provider>
            </div>
          </div>

          <div class="boolean-question goal-question">
            <p class="boolean-question__label">
              6. ¿Demuestra disposición para completar un objetivo vocacional?
            </p>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="model.isWillingToCompleteVocationalGoal"
                class="boolean-question__radio"
                custom-error-msg="Favor seleccionar Si o No."
                :error="errors[0]"
                :is-disabled="readOnly"
                is-inline
                name="vocational-goal-willingness"
                :options="yesNoOptions"
              />
            </validation-provider>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import CustomRadio from '@/components/common/CustomRadio';
import CustomTextArea from 'CustomTextArea';
import SectionTitle from 'SectionTitle';
import { getTapRequestSchoolGrade } from '@/services/api/tapRequest.api';
import documentsService from '@/services/documents';

export default {
  name: 'EducationalHistorySection',
  components: {
    CustomDropDown,
    CustomFileInput,
    CustomRadio,
    CustomTextArea,
    SectionTitle,
  },
  props: {
    readOnly: Boolean,
    value: {
      type: Object,
      default: () => null,
    },
    educationalHistoryInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      completedGradeProgramOptions: [],
      yesNoOptions: [
        {
          id: 1,
          label: 'Sí',
          value: true,
          check: false,
        },
        {
          id: 2,
          label: 'No',
          value: false,
          check: false,
        },
      ],
    };
  },
  computed: {
    model: {
      get() {
        return this.value || this.educationalHistoryInfo || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    'model.hasVocationalInterestEvaluationReport'(value) {
      if (!value) {
        this.removeVocationalInterestDocument();
      }
    },
  },
  mounted() {
    this.loadCompletedGradeProgramOptions();
  },
  methods: {
    async loadCompletedGradeProgramOptions() {
      const { data } = await getTapRequestSchoolGrade();

      this.completedGradeProgramOptions = (data || []).map((item) => ({
        id: item.id,
        name: item.edenGradeDescSpanish,
      }));
    },
    async downloadVocationalInterestDocument() {
      const documentId = this.model.vocationalInterestEvaluationDocument
        ?.documentId;
      if (!documentId) return;

      await documentsService.downloadFromServer(documentId);
    },
    removeVocationalInterestDocument() {
      const hasSavedDocument = !!this.model
        .vocationalInterestEvaluationDocument;
      this.model = {
        ...this.model,
        vocationalInterestEvaluationFile: null,
        vocationalInterestEvaluationDocument: null,
        vocationalInterestEvaluationDocumentDeleted: hasSavedDocument,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.educational-history-info {
  background: #fdfdfb;
  color: #111;
  padding: 8px 18px 24px;
}

.section-panel {
  border: 1px solid #dfe4e1;
  border-radius: 6px;
  margin-bottom: 24px;
  overflow: visible;
  position: relative;
}

.section-panel__title {
  background: #f4f5f4;
  border-bottom: 1px solid #dfe4e1;
  color: #17342f;
  font-size: 15px;
  font-weight: 700;
  padding: 15px 18px;
}

.section-panel__body {
  background: #fafbfa;
  padding: 22px 22px 24px;
}

.boolean-question {
  align-items: center;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 42px;
  padding: 6px 0;
}

.boolean-question__label {
  color: #111;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
}

.grade-field {
  margin: 22px 0 20px;
  max-width: 320px;
}

.question-textarea {
  border-top: 1px solid #e5e9e7;
  padding-top: 18px;
}

.question-textarea--spaced {
  margin-top: 22px;
}

.report-question {
  margin-top: 22px;
}

.document-row {
  align-items: end;
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(220px, 1fr) minmax(320px, 1fr);
  margin-top: 14px;
}

.document-row__label {
  color: #111;
  display: block;
  font-size: 13px;
  margin-bottom: 8px;
}

.document-row__value {
  color: #111;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
}

.goal-question {
  border-top: 1px solid #e5e9e7;
  margin-top: 22px;
  padding-top: 18px;
}

::v-deep .boolean-question__radio {
  min-width: 110px;

  > .col-12 {
    align-items: center;
    display: flex;
    gap: 18px;
    justify-content: flex-end;
    padding: 0;
  }

  .custom-control {
    margin: 0;
    min-height: 18px;
    padding-left: 1.35rem;
  }

  .custom-control-label {
    color: #111;
    font-size: 14px;
    line-height: 1.25;
  }

  .custom-control-label::before,
  .custom-control-label::after {
    height: 16px;
    left: -1.35rem;
    top: 0.05rem;
    width: 16px;
  }

  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #2db782;
    border-color: #2db782;
  }

  .custom-control-input.custom-control-input:disabled:checked
    ~ .custom-control-label::before {
    background-color: #3f5b60 !important;
    border-color: #3f5b60 !important;
  }
}

::v-deep .question-textarea textarea {
  min-height: 76px;
}

::v-deep .question-textarea .custom-input-label,
::v-deep .grade-field .custom-input-label,
::v-deep .grade-field .data-label,
::v-deep .grade-field .data-name,
::v-deep .educational-history-info .grade-field label,
::v-deep .educational-history-info .grade-field .data-label,
::v-deep .educational-history-info .grade-field .data-name,
::v-deep .educational-history-info .grade-field .custom-input-label,
::v-deep .educational-history-info .grade-field .custom-drop-down label,
::v-deep .document-file .custom-file-input-label,
::v-deep .document-file .input-label,
::v-deep .document-file label,
::v-deep .data-label,
::v-deep .custom-input-label,
::v-deep .input-label {
  color: #111 !important;
  font-size: 13px;
  font-weight: 500;
}

::v-deep .question-textarea,
::v-deep .question-textarea label,
::v-deep .question-textarea span,
::v-deep .question-textarea .text-right {
  color: #111 !important;
}

::v-deep .file-input-container {
  min-height: 38px;
}

@media (max-width: 767.98px) {
  .educational-history-info {
    padding: 8px 0 16px;
  }

  .boolean-question,
  .document-row {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  ::v-deep .boolean-question__radio > .col-12 {
    justify-content: flex-start;
  }
}
</style>
